// Connexions & usage — the vocabulary shared by the tracker (server)
// and the heartbeat (client) so both name the same things.
//
// The unit tracked is an "area": one functional zone of the app, not
// a URL. The client heartbeat sends the area it is displaying; the
// server also derives one from the API path so usage is still
// recorded when a tab makes calls without pinging (or when the ping
// is blocked). Both must agree on the vocabulary — hence this file.
export const USAGE_AREAS = [
    'dashboard', 'bureau', 'entry', 'monthly', 'inspect', 'conges', 'recup',
    'contrats', 'documents', 'reports', 'employees', 'org',
    'salaires', 'evaluations', 'recrutement', 'activity', 'settings',
    'espace', 'autre',
];
export const USAGE_AREA_LABEL = {
    dashboard: 'Tableau de bord',
    bureau: 'Bureau',
    entry: 'Saisie quotidienne',
    monthly: 'Grille mensuelle',
    inspect: 'Inspection',
    conges: 'Congés',
    recup: 'Récupération',
    contrats: 'Contrats',
    documents: 'Documents',
    reports: 'Rapports',
    employees: 'Employés',
    org: 'Organigramme',
    salaires: 'Salaires & bonus',
    evaluations: 'Évaluations',
    recrutement: 'Recrutement',
    activity: 'Journal d’activité',
    settings: 'Paramètres',
    espace: 'Espace employé',
    autre: 'Autre',
};
export function isUsageArea(v) {
    return typeof v === 'string' && USAGE_AREAS.includes(v);
}
// ─── Tracking tuning ─────────────────────────────────────────
// Active time is the sum of the gaps between two signs of life, but
// only while those gaps stay short: a 3-hour gap is a lunch break,
// not usage. Anything longer than IDLE_GAP_MS is dropped and the
// request is credited MIN_TICK_MS instead — so an isolated call
// (a PDF download, a cron-triggered fetch) still counts as "used
// the app briefly" without inflating the total.
export const USAGE_IDLE_GAP_MS = 5 * 60_000;
// Credit given to a sign of life that follows an idle gap.
export const USAGE_MIN_TICK_MS = 15_000;
// Client heartbeat cadence while the tab is visible. Comfortably
// under IDLE_GAP so a user reading a screen keeps accruing time.
export const USAGE_PING_MS = 60_000;
// A session with no sign of life for this long is considered over:
// the "en ligne" strip drops it and housekeeping closes it with
// endedAt = lastSeenAt (never "now" — the idle time is not usage).
export const USAGE_STALE_MS = 15 * 60_000;
// Window used by the live strip ("en ligne maintenant").
export const USAGE_ONLINE_MS = 5 * 60_000;
// ─── Path → area ─────────────────────────────────────────────
// Client-side: a router pathname ("/monthly", "/settings/hours").
export function usageAreaFromPath(pathname) {
    const seg = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
    switch (seg) {
        case '':
        case 'dashboard': return 'dashboard';
        case 'bureau': return 'bureau';
        case 'entry': return 'entry';
        case 'monthly': return 'monthly';
        case 'inspect': return 'inspect';
        case 'balances': return 'conges';
        case 'recup': return 'recup';
        case 'contrats': return 'contrats';
        case 'documents': return 'documents';
        case 'reports': return 'reports';
        case 'employees': return 'employees';
        case 'organigramme': return 'org';
        case 'salaires': return 'salaires';
        case 'evaluations': return 'evaluations';
        case 'recrutement': return 'recrutement';
        case 'activity': return 'activity';
        case 'settings': return 'settings';
        case 'espace': return 'espace';
        default: return 'autre';
    }
}
// Server-side fallback: an API path as seen by the tracker
// middleware ("/api/monthly-grid/2026-08"). Deliberately coarse —
// it only has to be right about which screen the caller is on.
const API_AREA_PREFIXES = [
    ['/api/dashboard', 'dashboard'],
    ['/api/bureau', 'bureau'],
    ['/api/attendance', 'entry'],
    ['/api/monthly-grid', 'monthly'],
    ['/api/inspect', 'inspect'],
    ['/api/leaves', 'conges'],
    ['/api/contracts', 'contrats'],
    ['/api/documents', 'documents'],
    ['/api/reports', 'reports'],
    ['/api/followups', 'reports'],
    ['/api/employees', 'employees'],
    ['/api/entities', 'employees'],
    ['/api/org', 'org'],
    ['/api/payroll', 'salaires'],
    ['/api/evaluations', 'evaluations'],
    ['/api/recrutement', 'recrutement'],
    ['/api/activity', 'activity'],
    ['/api/usage', 'settings'],
    ['/api/settings', 'settings'],
    ['/api/users', 'settings'],
    ['/api/decision-types', 'settings'],
    ['/api/postes', 'settings'],
    ['/api/holidays', 'settings'],
    ['/api/me', 'espace'],
];
export function usageAreaFromApiPath(path) {
    for (const [prefix, area] of API_AREA_PREFIXES) {
        if (path === prefix || path.startsWith(`${prefix}/`))
            return area;
    }
    return 'autre';
}
// ─── Device label ────────────────────────────────────────────
// A short "OS · Navigateur" label from a User-Agent string. Not a
// UA-parsing library and not trying to be: the boss screen wants to
// tell a phone from a desktop, not fingerprint anyone.
export function deviceLabel(ua) {
    if (!ua)
        return null;
    const os = /iPhone|iPod/i.test(ua) ? 'iPhone' :
        /iPad/i.test(ua) ? 'iPad' :
            /Android/i.test(ua) ? 'Android' :
                /Windows/i.test(ua) ? 'Windows' :
                    /Mac OS X|Macintosh/i.test(ua) ? 'Mac' :
                        /Linux/i.test(ua) ? 'Linux' : null;
    const browser = /Edg\//i.test(ua) ? 'Edge' :
        /OPR\/|Opera/i.test(ua) ? 'Opera' :
            /Chrome\//i.test(ua) && !/Chromium/i.test(ua) ? 'Chrome' :
                /Firefox\//i.test(ua) ? 'Firefox' :
                    /Safari\//i.test(ua) ? 'Safari' : null;
    if (!os && !browser)
        return null;
    return [os, browser].filter(Boolean).join(' · ');
}
//# sourceMappingURL=usage.js.map